import { EnumColorPreset, FormatPreset, FormBlockType, FormInputType } from '@doss/prisma/types'
import { z } from 'zod'

/**
 * Form Block Configuration Schemas
 *
 * This file contains the canonical Zod schemas for Form v2 blocks and inputs.
 * These schemas define the structure and validation rules for form block configurations
 * used across web components and backend processing.
 */

// =============================================================================
// Core Types and Enums
// =============================================================================

export type FormArrayOption = {
  value: string
  label: string
  colorPreset?: EnumColorPreset
  colorCustom?: string
  iconName?: string
}

/** Types for generating form V2*/
export type BarcodeScannerType = 'none' | 'mobile' | 'external'

/** Formats read by the mobile camera: all formats, barcodes other than QR, or QR only */
export type BarcodeScanFormat = 'all' | 'barcode' | 'qr'

export type InputMetadata = {
  placeholder?: string
  options?: FormArrayOption[]
  linkUserToSignature?: boolean
  displayImagesInline?: boolean
  barcodeScannerType?: BarcodeScannerType
  barcodeFormats?: BarcodeScanFormat
}

// Text element semantic types
export enum FormBannerAlertType {
  INFO = 'Info',
  NEUTRAL = 'Neutral',
  SUCCESS = 'Success',
  WARNING = 'Warning',
  ERROR = 'Error',
}

// =============================================================================
// Lookup Column Schema
// =============================================================================

export const lookupColumnSchema = z.object({
  table: z.object({
    name: z.string(),
  }),
  column: z.object({
    name: z.string(),
  }),
  group: z.number(),
  lookupFilterExpression: z.string().optional(),
})

// =============================================================================
// Validation Rules Schema
// =============================================================================

const validationRuleSchema = z.object({
  conditionExpression: z.string(),
  validationMessage: z.string().optional(),
})

// =============================================================================
// Visibility Condition Schema
// =============================================================================

export const visibilityConditionSchema = z.object({
  name: z.string(),
  slug: z.string(),
  conditionExpression: z.string(),
})

// =============================================================================
// Form Input Schema
// =============================================================================

export const formInputSchema = z.object({
  label: z.string(),
  slug: z.string().optional(),
  description: z.string().optional(),
  inputType: z.nativeEnum(FormInputType),
  isRequired: z.boolean(),
  isReadOnly: z.boolean(),
  isVisible: z.boolean(),
  isFrozen: z.boolean(),
  isAutoFill: z.boolean(),
  isMultiSelect: z.boolean(),
  defaultValue: z.unknown().nullable(),
  formatPreset: z.nativeEnum(FormatPreset).nullable().optional(),
  formatOptions: z.record(z.string(), z.unknown()).nullable().optional(),
  options: z.array(z.string()).optional(),
  lookupColumn: lookupColumnSchema.optional(),
  validationRules: z.array(validationRuleSchema).optional(),
})

// =============================================================================
// Block Metadata Schemas
// =============================================================================

// TEXT_ELEMENT specific metadata
export enum LayoutType {
  BANNER = 'BANNER',
  HEADER = 'HEADER',
  DESCRIPTION = 'DESCRIPTION',
}

const bannerMetadataSchema = z.object({
  type: z.literal(LayoutType.BANNER),
  text: z.string(),
  title: z.string(),
  semanticType: z.nativeEnum(FormBannerAlertType).optional(),
})
const headerMetadataSchema = z.object({
  type: z.literal(LayoutType.HEADER),
  title: z.string(),
})
const descriptionMetadataSchema = z.object({
  type: z.literal(LayoutType.DESCRIPTION),
  text: z.string(),
})

export const textElementMetadataSchema = z.union([
  bannerMetadataSchema,
  headerMetadataSchema,
  descriptionMetadataSchema,
])

// TABLE specific metadata
export const tableMetadataSchema = headerMetadataSchema.extend({
  formInputGroupLabel: z.string().optional(),
})

export const sectionMetadataSchema = z.object({ title: z.string().optional() })

// =============================================================================
// Block Type Definitions
// =============================================================================

// Base block schema for common properties
export const baseBlockSchema = z.object({
  blockType: z.nativeEnum(FormBlockType),
  metadata: z.record(z.string(), z.unknown()).optional(),
  visibilityConditions: z.array(z.string()).optional(),
})

// INPUT block schema
export const inputBlockSchema = baseBlockSchema.extend({
  blockType: z.literal(FormBlockType.INPUT),
  input: formInputSchema,
})

// TEXT_ELEMENT block schema
export const textElementBlockSchema = baseBlockSchema.extend({
  blockType: z.literal(FormBlockType.TEXT_ELEMENT),
  metadata: textElementMetadataSchema.optional(),
})

// DIVIDER block schema
export const dividerBlockSchema = baseBlockSchema.extend({
  blockType: z.literal(FormBlockType.DIVIDER),
})

// TABLE block schema
export const tableBlockSchema = baseBlockSchema.extend({
  blockType: z.literal(FormBlockType.TABLE),
  formInputGroupLabel: z.string(),
  slug: z.string().optional(),
  childBlocks: z.array(inputBlockSchema),
  metadata: tableMetadataSchema.optional(),
})

// SECTION block schema (has child blocks)
export const sectionBlockSchema: z.ZodType<{
  blockType: typeof FormBlockType.SECTION
  metadata?: z.infer<typeof sectionMetadataSchema>
  visibilityConditions?: string[]
  childBlocks: Block[]
}> = baseBlockSchema.extend({
  blockType: z.literal(FormBlockType.SECTION),
  metadata: sectionMetadataSchema.optional(),
  childBlocks: z.array(z.lazy(() => blockSchema)),
})

// ROW block schema (has child blocks)
export const rowBlockSchema: z.ZodType<{
  blockType: typeof FormBlockType.ROW
  visibilityConditions?: string[]
  childBlocks: Block[]
}> = baseBlockSchema.extend({
  blockType: z.literal(FormBlockType.ROW),
  childBlocks: z.array(inputBlockSchema),
})

// Define block types for recursive schema
export type Block =
  | z.infer<typeof inputBlockSchema>
  | z.infer<typeof textElementBlockSchema>
  | z.infer<typeof dividerBlockSchema>
  | z.infer<typeof tableBlockSchema>
  | z.infer<typeof rowBlockSchema>
  | z.infer<typeof sectionBlockSchema>

// Union of all possible block types
export const blockSchema: z.ZodType<Block> = z.union([
  inputBlockSchema,
  textElementBlockSchema,
  dividerBlockSchema,
  tableBlockSchema,
  rowBlockSchema,
  sectionBlockSchema,
])

// =============================================================================
// Main Form Schema
// =============================================================================

export const form2UploadJsonSchema = z.object({
  formName: z.string(),
  sections: z.array(sectionBlockSchema),
  visibilityConditions: z.array(visibilityConditionSchema).optional(),
})

// =============================================================================
// Type Exports
// =============================================================================

export type LookupColumn = z.infer<typeof lookupColumnSchema>
export type ValidationRule = z.infer<typeof validationRuleSchema>
export type VisibilityCondition = z.infer<typeof visibilityConditionSchema>

export type TextElementMetadata = z.infer<typeof textElementMetadataSchema>
export type TableMetadata = z.infer<typeof tableMetadataSchema>
export type SectionMetadata = z.infer<typeof sectionMetadataSchema>

export type BaseBlock = z.infer<typeof baseBlockSchema>
export type InputBlock = z.infer<typeof inputBlockSchema>
export type TextElementBlock = z.infer<typeof textElementBlockSchema>
export type DividerBlock = z.infer<typeof dividerBlockSchema>
export type TableBlock = z.infer<typeof tableBlockSchema>
export type RowBlock = z.infer<typeof rowBlockSchema>
export type SectionBlock = z.infer<typeof sectionBlockSchema>

export type Form2UploadJson = z.infer<typeof form2UploadJsonSchema>

// Additional type aliases for common usage
export type FormInput = z.infer<typeof formInputSchema>
